import React, { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import cls from './PositionCircle.module.css';
import { connectionActions } from '../../../store/reducers/connectionSlice/connectionSlice';
import { useDispatch, useSelector } from 'react-redux';
import { positionSelector } from '../../../store/reducers/positionSlice/positionSlice';
import moment from 'moment';
import { getUpdatePositions } from '../../../helpers/getCountDays';
import { useUpdateAll } from '../../../hooks/useUpdateAll';
import { stageSelector } from '../../../store/reducers/stageSlice/stageSlice';
import { charterSelector } from '../../../store/reducers/charterSlice/charterSlice';
import { queueFetch } from '../../../helpers/queueFetch';
import { checkObsession } from '../../../helpers/checkObsession';
import { getChainConnection } from '../../../helpers/getChainConnection';
import { positionsConnectionsActions } from '../../../store/reducers/positionsConnections/positionsConnectionsSlice';

export const PositionCircle = ({ classes, position, positionCircle, widthPosition, setIsShow }) => {
    const ref = useRef(null);
    const dispatch = useDispatch();
    const allStage = useSelector(stageSelector.selectAll);
    const allCharters = useSelector(charterSelector.selectAll);
    const parentCharter = allCharters.find((charter) => charter.children.includes(position.guid));
    const parentStage = allStage.find((stage) => {
        const childGuid = parentCharter ? parentCharter.guid : position.guid;
        return stage.positions.includes(childGuid);
    });
    const { updateAll } = useUpdateAll(parentStage);
    const allPositions = useSelector(positionSelector.selectEntities);

    const connection = useSelector((state) => state.connectionReducer);

    const handler = () => {
        if (connection.isConnection) {
            dispatch(connectionActions.endConnection());
        }
    };
    useEffect(() => {
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [connection]);

    const addConnection = (connectionData) => {
        const queue = [];

        const positionFromGuid = connectionData.guid;
        const positionToGuid = position.guid;

        const resultEntities = {};
        const currentPosition = allPositions[connectionData.guid];

        const connectionGuids = currentPosition.connections.map(({ from }) => from);

        if (positionCircle === 'left' && connection.first.type === 1) {
            return;
        }

        if (positionCircle === 'right' && connection.first.type === 0) {
            return;
        }

        if (connectionGuids.includes(position.guid)) {
            setIsShow(true);
            return;
        }

        if (currentPosition.guid === position.guid) {
            setIsShow(true);
            return;
        }

        const isObsession = checkObsession(currentPosition, allPositions, position);

        if (isObsession) {
            setIsShow(true);
            return;
        }

        const newConnection = {
            from: currentPosition.guid,
            to: position.guid,
            type: connectionData.type,
        };

        const updatedConnections = [...currentPosition.connections, newConnection];
        const updatedConnectionsTo = [...position.connections, newConnection];

        const updatedPositionFrom = {
            ...currentPosition,
            connections: updatedConnections,
        };
        const updatedPositionTo = { ...position, connections: updatedConnectionsTo };

        const diffBeforePosition =
            moment(updatedPositionTo.planned.start_date).diff(
                moment(updatedPositionFrom.planned.end_date),
                'days'
            ) - 1;

        let chainConnection = getChainConnection(updatedPositionTo, allPositions);

        let startDateTo;

        let endDateTo;

        const prevPositions = position.connections
            .filter((connection) => connection.type === 0 && connection.from !== position.guid)
            .map((connection) => allPositions[connection.from])
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            );

        const lastPrevPosition = prevPositions.slice(-1)[0];

        const predecessorConnections = updatedPositionTo.connections.filter(
            (connection) => connection.type === 1 && connection.to === position.guid
        );

        const firstPredecessorStartDate = predecessorConnections
            .map((connection) => allPositions[connection.from])
            .sort(
                (positionA, positionB) =>
                    moment(positionA.planned.end_date).valueOf() -
                    moment(positionB.planned.end_date).valueOf()
            )[0]?.planned.start_date;

        if (connectionData.type === 0) {
            startDateTo = moment(updatedPositionFrom.planned.start_date)
                .add(updatedPositionFrom.planned.days_count, 'days')
                .format();

            endDateTo = moment(startDateTo)
                .add(+updatedPositionTo.planned.days_count - 1, 'days')
                .format();

            if (lastPrevPosition) {
                startDateTo = moment(lastPrevPosition.planned.start_date)
                    .add(lastPrevPosition.planned.days_count, 'days')
                    .format();

                endDateTo = moment(startDateTo)
                    .add(+updatedPositionTo.planned.days_count - 1, 'days')
                    .format();
            }
        } else {
            startDateTo = moment(updatedPositionFrom.planned.start_date)
                .add(-Number(updatedPositionTo.planned.days_count), 'days')
                .format();

            endDateTo = moment(startDateTo)
                .add(+updatedPositionTo.planned.days_count - 1, 'days')
                .format();

            if (firstPredecessorStartDate) {
                startDateTo = moment(firstPredecessorStartDate)
                    .add(-Number(updatedPositionTo.planned.days_count), 'days')
                    .format();

                endDateTo = moment(startDateTo)
                    .add(+updatedPositionTo.planned.days_count - 1, 'days')
                    .format();
            }
        }

        const plannedTo = {
            ...updatedPositionTo.planned,
            start_date: startDateTo,
            end_date: endDateTo,
        };

        queue.push({
            data: {
                data: {
                    guid: updatedPositionTo.guid,
                    startDate: moment(plannedTo.start_date).format('YYYY-MM-DD'),
                    endDate: moment(plannedTo.end_date).format('YYYY-MM-DD'),
                },
                type: 'update',
                action: 'add_connections',
            },
            subEntityType: 'step',
            subEntityId: parentStage.id,
            t: Date.now(),
        });

        resultEntities[updatedPositionFrom.guid] = updatedPositionFrom;
        resultEntities[updatedPositionTo.guid] = { ...updatedPositionTo, planned: plannedTo };

        if (diffBeforePosition < 0) {
            const diff = Math.abs(diffBeforePosition);

            const updateNextPosition = getUpdatePositions(chainConnection, diff);

            updateNextPosition.forEach((position) => {
                queue.push({
                    data: {
                        data: {
                            guid: position.changes.guid,
                            startDate: moment(position.changes.planned.start_date).format(
                                'YYYY-MM-DD'
                            ),
                            endDate: moment(position.changes.planned.end_date).format('YYYY-MM-DD'),
                        },
                        type: 'update',
                        action: 'add_connections',
                    },
                    subEntityType: 'step',
                    subEntityId: parentStage.id,
                    t: Date.now(),
                });
            });

            updateNextPosition.forEach((item) => {
                resultEntities[item.id] = item.changes;
            });
        }

        queueFetch(queue);

        const queueConnections = [
            {
                data: {
                    data: {
                        connection: [
                            {
                                positionFromGuid: positionFromGuid,
                                positionToGuid: positionToGuid,
                                connectionType: connection.first.type,
                            },
                        ],
                    },
                    type: 'save_connections',
                    action: 'save_connections',
                },
                subEntityType: 'step',
                subEntityId: parentStage.id,
                t: Date.now(),
            },
        ];
        queueFetch(queueConnections);

        updateAll(resultEntities);

        dispatch(positionsConnectionsActions.addConnection(newConnection));
    };

    useEffect(() => {
        if (ref !== null) {
            const position = ref.current?.getBoundingClientRect();
        }
    }, [ref]);

    return (
        <div className={classNames([classes])}>
            <div
                className={classNames(cls.PositionCircle, {
                    [cls.active]:
                        positionCircle === connection.first.position &&
                        `${position.guid}` === connection.first.guid,
                    [cls.connecting]: connection.isConnection,
                })}
                ref={ref}
                onClick={(event) => {
                    event.stopPropagation();
                    if (!connection.isConnection) {
                        dispatch(
                            connectionActions.setFirst({
                                x: event.clientX,
                                y: event.clientY,
                                guid: `${position.guid}`,
                                position: positionCircle,
                                type: positionCircle === 'right' ? 0 : 1,
                            })
                        );
                    } else {
                        dispatch(connectionActions.setSecond({ positionWidth: widthPosition }));
                        addConnection(connection.first);
                        dispatch(connectionActions.endConnection());
                    }
                }}
            ></div>
        </div>
    );
};
